import type { ErrorEvent, EventHint } from '@sentry/angular';

export type SentryErrorProbe =
  | 'angular_error'
  | 'browser_error'
  | 'unhandled_rejection';
const probes = new WeakMap<
  Error,
  { scenario: SentryErrorProbe; runId: string; onSdkCapture?: () => void }
>();
const messages: Record<SentryErrorProbe, string> = {
  angular_error: 'Sentry verification: Angular event handler',
  browser_error: 'Sentry verification: browser error',
  unhandled_rejection: 'Sentry verification: unhandled rejection',
};

export function createSentryErrorProbe(
  scenario: SentryErrorProbe,
  runId: string,
  onSdkCapture?: () => void,
): Error {
  const error = new Error(messages[scenario]);
  probes.set(error, { scenario, runId, onSdkCapture });
  return error;
}

/** Marks only objects created by the guarded test panel. This is not an error-data filter. */
export function markSentryErrorProbe(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent {
  const original = hint.originalException;
  const probe = original instanceof Error ? probes.get(original) : undefined;
  if (!probe) return event;
  const notify = probe.onSdkCapture;
  probe.onSdkCapture = undefined;
  try {
    notify?.();
  } catch {
    /* Test feedback must not interfere with reporting. */
  }
  return {
    ...event,
    tags: {
      ...event.tags,
      source: 'sentry_test',
      test_run_id: probe.runId,
      test_scenario: probe.scenario,
    },
  };
}
